import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Backdrop,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  ButtonBase
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Cancel, Visibility, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from '../../../services/axios';
import { debounce } from 'lodash';

function ListaVendedor() {
  const navigate = useNavigate();

  // Estados de filtro
  const [termoBusca, setTermoBusca] = useState('');
  const [isAtivo, setIsAtivo] = useState(true);

  // Estados de listagem/paginação
  const [vendedores, setVendedores] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [loading, setLoading] = useState(false);

  // Configurações
  const registrosPorPagina = 10;
  const API_URL = '/api/vendedores'; // Ajuste conforme seu endpoint real

  // Função que chama o back-end para buscar vendedores
  const buscarVendedores = async (busca, ativo, page) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/buscar`, {
        params: {
          q: busca,
          ativo,
          page: page - 1, // zero-based
          size: registrosPorPagina
        }
      });
      // Se o back-end retornar { content: [...], totalPages: N }
      setVendedores(data.content);
      setTotalPaginas(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar vendedores:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce para evitar chamadas excessivas enquanto o usuário digita
  const debouncedBuscar = useCallback(
    debounce((valorBusca, valorAtivo, paginaAtual) => {
      buscarVendedores(valorBusca, valorAtivo, paginaAtual);
    }, 500),
    []
  );

  // useEffect para disparar a busca ao alterar filtros ou página
  useEffect(() => {
    debouncedBuscar(termoBusca, isAtivo, pagina);
    return debouncedBuscar.cancel;
  }, [termoBusca, isAtivo, pagina, debouncedBuscar]);

  // Ao mudar a página
  const handleChangePagina = (event, value) => {
    setPagina(value);
  };

  // Ao digitar no campo de busca
  const handleTermoBuscaChange = (e) => {
    setTermoBusca(e.target.value);
  };

  // Checkbox "Ativo?"
  const handleAtivoChange = (e) => {
    setIsAtivo(e.target.checked);
    setPagina(1); // Reseta para a primeira página
  };

  // Botão "Limpar"
  const limparBusca = () => {
    setTermoBusca('');
    setIsAtivo(true);
    setPagina(1);
  };

  // Botão "Novo"
  const handleNovo = () => {
    navigate('/vendedor/edit'); // Ajuste a rota conforme necessário
  };

  // Botão "Editar" na grid
  const handleEditar = (id) => {
    navigate(`/vendedor/edit/${id}`);
  };

  // Botão "Excluir" na grid
  const handleExcluir = async (id) => {
    console.log(`Excluir vendedor com ID: ${id}`);
    // Exemplo de exclusão:
    // await axios.delete(`${API_URL}/${id}`);
    // buscarVendedores(termoBusca, isAtivo, pagina);
  };

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      {/* Backdrop de carregamento */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Título da página */}
      <Typography variant="h4" align="center" gutterBottom>
        Listagem de Pessoa: Vendedor
      </Typography>

      {/* Card de Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={<Checkbox checked={isAtivo} onChange={handleAtivoChange} />}
                label="Ativo?"
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <Button variant="contained" onClick={limparBusca} fullWidth>
                Limpar
              </Button>
            </Grid>
            <Grid item xs={12} sm={9}>
              <TextField
                label="Nome, Fantasia, CPF, CNPJ"
                variant="outlined"
                fullWidth
                value={termoBusca}
                onChange={handleTermoBuscaChange}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dica de busca (CPF/CNPJ) */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" align="center">
          Para localizar por CPF/CNPJ, digite somente os números.
        </Typography>
      </Box>

      {/* Área de Ações: Botão "+ Novo" centralizado */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <Button variant="contained" onClick={handleNovo}>
          + Novo
        </Button>
      </Box>

      {/* Tabela de resultados */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Nome / Fantasia</strong></TableCell>
              <TableCell><strong>Tipo</strong></TableCell>
              <TableCell><strong>CPF / CNPJ</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendedores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum vendedor encontrado.
                </TableCell>
              </TableRow>
            ) : (
              vendedores.map((vendedor, index) => (
                <TableRow key={index}>
                  <TableCell>{vendedor.id || vendedor.codigo}</TableCell>
                  <TableCell>{vendedor.nome}</TableCell>
                  <TableCell>{vendedor.fisica ? 'Física' : 'Jurídica'}</TableCell>
                  <TableCell>{vendedor.documento}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      {/* Botão Editar: ícone e texto clicáveis */}
                      <ButtonBase
                        onClick={() => navigate(`/pessoa/transportadora/edit/${vendedor.id}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'row', // força que seja sempre linha
                          justifyContent: 'center',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <Edit fontSize="small" sx={{ color: 'orange' }} /> <br />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'orange' }}>
                          EDITAR
                        </Typography>
                      </ButtonBase>

                      {/* Botão Consultar */}
                      <ButtonBase
                        onClick={() => navigate(`/pessoa/transportadora/edit/${vendedor.id}`)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <Visibility fontSize="small" sx={{ color: 'blue' }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'blue' }}>
                          CONSULTAR
                        </Typography>
                      </ButtonBase>

                      {/* Botão Deletar */}
                      <ButtonBase
                        //   onClick={() => confirmDelete(cliente.id)}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1,
                        }}
                      >
                        <Cancel fontSize="small" sx={{ color: 'red' }} />
                        <Typography variant="caption" sx={{ mt: 0.5, color: 'red' }}>
                          DELETAR
                        </Typography>
                      </ButtonBase>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPaginas}
          page={pagina}
          onChange={handleChangePagina}
          color="primary"
        />
      </Box>
    </Box>
  );
}

export default ListaVendedor;
